import React, { useRef, useState, useEffect } from 'react';
import classNames from 'classnames';

import Field from '../../Field';
import VideoBg from '../../Primitives/VideoBg/VideoBg';
import { IconVideoPlay } from '../../../../components';

import css from './SectionContainer.module.css';

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * This component can be used to wrap some common styles and features of Section-level components.
 * E.g: const SectionHero = props => (<SectionContainer><H1>Hello World!</H1></SectionContainer>);
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.id id of the section
 * @param {string?} props.as tag/element name. Defaults to 'section'.
 * @param {ReactNode} props.children
 * @param {Object} props.appearance
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} containing wrapper that can be used inside Block components.
 */
const SectionContainer = props => {
  const {
    className,
    rootClassName,
    id,
    as,
    children,
    appearance,
    options,
    pageId,
    experienceRewildingBg,
    ...otherProps
  } = props;

  const Tag = as || 'section';
  const classes = classNames(rootClassName || css.root, className);
  const videoRef = useRef(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(true);

  const videoBgData = {
    "landing-page": "/static/videos/wilder-places-hero2.mp4",

    "landscape-iberian-highlands": "/static/videos/iberian-highlands.mp4 ",
    "landscape-greater-coa-valley": "/static/videos/greater-coa-valley.mp4 ",
    "landscape-central-apennines": "/static/videos/central-apennines.mp4",
    "landscape-southern-carpathians": "/static/videos/southern-carpathians.mp4",

    "landscape-affric-highlands": "/static/videos/affric-highlands.mp4",
    "landscape-danube-delta": "/static/videos/danube-delta.mp4",
    "landscape-rhodope-mountains": "/static/videos/rhodope-mountains.mp4",
    "landscape-velebit-mountains": "/static/videos/velebit-mountains.mp4",
    "landscape-nordic-taiga": "/static/videos/nordic-taiga.mp4",
    "landscape-dauphine-alps": "/static/videos/dauphine-alps.mp4",

    "landscape-oder-delta": "/static/videos/oder-delta.mp4",
  };

  const handleVideoButtonClick = () => {
    if (videoRef.current && id === "landscape-hero") {
      videoRef.current.togglePlayPause();
      setTimeout(() => {
        if (videoRef.current) {
          setIsVideoPlaying(videoRef.current.isPlaying);
        }
      }, 10);
    }
  };

  return (
    <Tag className={classes} id={id} {...otherProps}>
      {appearance?.fieldType === 'customAppearance' ? (
        <>
          {id !== 'marketplace-introduction' && id !== "landscape-hero" ? (
            <Field
              data={{ alt: `Background image for ${id}`, ...appearance }}
              className={className}
              options={options}
              experienceRewildingBg={experienceRewildingBg}
            />
          ) : (
            <>
              <div className={classNames(css.imageBg, !videoBgData?.[pageId] && css.imageBgShown)}>
                <Field
                  data={{ alt: `Background image for ${id}`, ...appearance }}
                  className={className}
                  options={options}
                />
              </div>
              <div className={css.videoBg}>
                <VideoBg
                  ref={videoRef}
                  src={videoBgData[pageId]}
                  type="video/mp4"
                  isLandscapeHero={id === "landscape-hero"}
                />

                {id === "landscape-hero" && (
                  <div className={css.videoButtonWrapper} onClick={handleVideoButtonClick}>
                    <div className={css.videoButton}>
                      <IconVideoPlay type={isVideoPlaying ? "stop" : "play"} />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      ) : null}

      <div className={css.sectionContent}>{children}</div>
    </Tag>
  );
};

export default SectionContainer;
